export type ViewerFolder = { id: string; name: string; path: string; parent_id?: string | null };
export type ViewerDoc = {
  id: string;
  filename: string;
  folder_id: string | null;
  display_name?: string;
  isNew?: boolean;
  folder_name?: string;
  snippet?: string;
  matchType?: string;
};

export type FolderTreeNode = {
  folder: ViewerFolder;
  children: FolderTreeNode[];
  documents: ViewerDoc[];
};

export function buildFolderTree(folders: ViewerFolder[], documents: ViewerDoc[]) {
  const byParent = new Map<string | null, ViewerFolder[]>();
  for (const f of folders) {
    const key = f.parent_id ?? null;
    if (!byParent.has(key)) byParent.set(key, []);
    byParent.get(key)!.push(f);
  }
  for (const list of byParent.values()) list.sort((a, b) => a.name.localeCompare(b.name));

  const docsByFolder = new Map<string | null, ViewerDoc[]>();
  for (const d of documents) {
    const key = d.folder_id ?? null;
    if (!docsByFolder.has(key)) docsByFolder.set(key, []);
    docsByFolder.get(key)!.push(d);
  }
  for (const list of docsByFolder.values()) list.sort((a, b) => a.filename.localeCompare(b.filename));

  function build(parentId: string | null): FolderTreeNode[] {
    return (byParent.get(parentId) ?? []).map((folder) => ({
      folder,
      children: build(folder.id),
      documents: docsByFolder.get(folder.id) ?? [],
    }));
  }

  return {
    roots: build(null),
    rootDocs: docsByFolder.get(null) ?? [],
  };
}

export function ancestorIds(folderId: string | null, folders: ViewerFolder[]): string[] {
  if (!folderId) return [];
  const byId = new Map(folders.map((f) => [f.id, f]));
  const chain: string[] = [];
  let cur: string | null = folderId;
  while (cur) {
    chain.unshift(cur);
    cur = byId.get(cur)?.parent_id ?? null;
  }
  return chain;
}

export function folderContainsDoc(node: FolderTreeNode, docId: string | null): boolean {
  if (!docId) return false;
  if (node.documents.some((d) => d.id === docId)) return true;
  return node.children.some((c) => folderContainsDoc(c, docId));
}
